import React, { useState } from 'react';
import { Modal, Button } from '../ui';
import { Copy, CheckCircle2, Printer, Mail, MessageSquare, FileText, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface BoardPitchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BoardPitchModal: React.FC<BoardPitchModalProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'email' | 'chat' | 'memo'>('email');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const emailSubject = "Proposal: Simplifying our dues, roster & communication with OrgSets";
  const emailBody = `Hi [President Name] and [Treasurer Name],

I wanted to share a platform I've been evaluating for our organization called OrgSets (https://orgsets.com).

Right now, our operations are split across multiple disjointed tools—Mailchimp for newsletters, Venmo/spreadsheets for dues, Google Drive for bylaws, and group chats for announcements. This costs us around $150/mo in software fees and creates manual reconciliation headaches every time dues come in.

OrgSets consolidates everything into a single command center specifically built for clubs and leagues:
1. Direct Stripe Payouts: Member dues deposit straight into our organization's checking account with an automated ledger for easy tax/accounting exports.
2. Free to Start: Zero fixed monthly software subscription (free for up to 50 members), saving us over $1,500/year.
3. Privacy & Compliance: Role-based permissions, an encrypted vault for our bylaws/waivers, and zero ads or member data selling.

They have a 1-page executive memo summarizing the numbers and security:
https://orgsets.com/board-memo

I recommend we test it with a free pilot for our next upcoming event or dues cycle. It takes 60 seconds to set up with no credit card required.

Let me know what you think,
[Your Name]`;

  const chatMessage = `Hey everyone — I've been researching ways to streamline our group's dues, roster, and announcements so we stop juggling Venmo, Mailchimp, and spreadsheets.

Found OrgSets (https://orgsets.com) — it's built specifically for local clubs and leagues. It deposits dues directly into our bank account via Stripe, includes a digital vault for waivers/bylaws, and is completely free to start (saving us ~$1,500/yr in SaaS subscriptions).

I put together a 1-page board brief here: https://orgsets.com/board-memo

Can we spend 5 minutes reviewing this at our next meeting?`;

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    toast.success('Copied to clipboard!');
    setTimeout(() => {
      setCopiedKey(null);
    }, 2500);
  };

  const handlePrint = () => {
    window.open('/board-memo?print=true', '_blank');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Board & Treasurer Approval Pack"
      className="md:max-w-2xl"
    >
      <div className="space-y-6">
        {/* Tab switcher */}
        <div className="flex border-[3px] border-black dark:border-white p-1 bg-gray-100 dark:bg-zinc-900 gap-1 font-mono text-xs">
          <button
            type="button"
            onClick={() => setActiveTab('email')}
            className={`flex-1 py-2.5 px-3 font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'email'
                ? 'bg-black text-white dark:bg-white dark:text-black shadow-[2px_2px_0px_#0044FF]'
                : 'text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white'
            }`}
          >
            <Mail size={14} />
            <span>Email to Board</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('chat')}
            className={`flex-1 py-2.5 px-3 font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'chat'
                ? 'bg-black text-white dark:bg-white dark:text-black shadow-[2px_2px_0px_#0044FF]'
                : 'text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white'
            }`}
          >
            <MessageSquare size={14} />
            <span>Chat Blurb</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('memo')}
            className={`flex-1 py-2.5 px-3 font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'memo'
                ? 'bg-black text-white dark:bg-white dark:text-black shadow-[2px_2px_0px_#0044FF]'
                : 'text-gray-600 dark:text-gray-300 hover:text-black dark:hover:text-white'
            }`}
          >
            <FileText size={14} />
            <span>1-Page Memo</span>
          </button>
        </div>

        {/* Tab 1: Email */}
        {activeTab === 'email' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-gray-500">
                Ready to send to President & Treasurer
              </span>
              <button
                type="button"
                onClick={() => copyToClipboard(`Subject: ${emailSubject}\n\n${emailBody}`, 'email')}
                className="flex items-center gap-1.5 text-xs font-mono font-bold px-3 py-1.5 bg-[#0044FF] text-white hover:bg-black transition-colors border border-black dark:border-white shadow-[2px_2px_0px_#000000]"
              >
                {copiedKey === 'email' ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                <span>{copiedKey === 'email' ? 'Copied!' : 'Copy Email'}</span>
              </button>
            </div>

            <div className="border-[3px] border-black dark:border-white bg-gray-50 dark:bg-zinc-950 p-4 font-mono text-xs text-black dark:text-white space-y-3">
              <div>
                <span className="text-gray-400 font-bold block uppercase text-[10px]">Subject</span>
                <span className="font-bold">{emailSubject}</span>
              </div>
              <div className="border-t border-black/10 dark:border-white/10 pt-2 whitespace-pre-line leading-relaxed">
                {emailBody}
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Chat */}
        {activeTab === 'chat' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-gray-500">
                For Board Slack, GroupMe, or WhatsApp
              </span>
              <button
                type="button"
                onClick={() => copyToClipboard(chatMessage, 'chat')}
                className="flex items-center gap-1.5 text-xs font-mono font-bold px-3 py-1.5 bg-[#0044FF] text-white hover:bg-black transition-colors border border-black dark:border-white shadow-[2px_2px_0px_#000000]"
              >
                {copiedKey === 'chat' ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                <span>{copiedKey === 'chat' ? 'Copied!' : 'Copy Message'}</span>
              </button>
            </div>

            <div className="border-[3px] border-black dark:border-white bg-gray-50 dark:bg-zinc-950 p-4 font-mono text-xs text-black dark:text-white whitespace-pre-line leading-relaxed">
              {chatMessage}
            </div>
          </div>
        )}

        {/* Tab 3: Memo preview & Print */}
        {activeTab === 'memo' && (
          <div className="space-y-4">
            <div className="border-[3px] border-black dark:border-white p-5 bg-white dark:bg-black shadow-[4px_4px_0px_#0044FF] space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono font-black uppercase tracking-wider bg-black text-white dark:bg-white dark:text-black px-2 py-0.5">
                  1-Page Executive Memo
                </span>
                <span className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400">
                  Ready for Print / PDF
                </span>
              </div>
              <h4 className="font-black text-base uppercase tracking-tight">
                Modernizing Our Organization Stack With OrgSets
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-300 font-sans leading-relaxed">
                A formal 1-page brief formatted specifically for executive board review. Details annual software cost savings (~$1,500/yr), direct Stripe Connect payouts to your bank, and data privacy safeguards.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                variant="primary"
                onClick={handlePrint}
                className="flex-1 h-12 rounded-none bg-black text-white dark:bg-white dark:text-black font-black uppercase text-xs tracking-wider flex items-center justify-center gap-2 hover:bg-[#0044FF] hover:text-white transition-colors border-[3px] border-black dark:border-white shadow-[4px_4px_0px_#000000]"
              >
                <Printer size={16} />
                <span>Print / Save 1-Page PDF</span>
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  window.location.href = '/board-memo';
                }}
                className="flex-1 h-12 rounded-none border-[3px] border-black dark:border-white text-black dark:text-white font-black uppercase text-xs tracking-wider flex items-center justify-center gap-2 hover:bg-[#FFD600] hover:text-black transition-colors"
              >
                <span>Read Full Memo</span>
                <ArrowRight size={16} />
              </Button>
            </div>
          </div>
        )}

        {/* Footer info strip */}
        <div className="pt-2 border-t-2 border-black/10 dark:border-white/10 flex items-center justify-between text-[11px] font-mono text-gray-500">
          <span>Direct Share Link:</span>
          <button
            type="button"
            onClick={() => copyToClipboard('https://orgsets.com/board-memo', 'link')}
            className="text-black dark:text-white font-bold hover:underline flex items-center gap-1"
          >
            <span>orgsets.com/board-memo</span>
            {copiedKey === 'link' ? <CheckCircle2 size={12} className="text-emerald-500" /> : <Copy size={12} />}
          </button>
        </div>
      </div>
    </Modal>
  );
};
